import { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UtensilsCrossed, Users, Download, RefreshCw } from 'lucide-react';
import api from '@/lib/api';
import { cur } from '@/lib/currency';
import { exportToCsv } from '@/lib/exportCsv';
import { toast } from 'sonner';

const nowMonth = () => new Date().toISOString().slice(0, 7);
const fmt = (v) => `${cur()} ${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function StaffFoodReportPage() {
  const [month, setMonth] = useState(nowMonth());
  const [branchId, setBranchId] = useState('all');
  const [branches, setBranches] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('summary'); // 'summary' | 'orders'
  const [orders, setOrders] = useState(null);
  const [loadingOrders, setLoadingOrders] = useState(false);

  useEffect(() => {
    api.get('/branches').then((r) => setBranches(r.data || [])).catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { month };
      if (branchId && branchId !== 'all') params.branch_id = branchId;
      const { data } = await api.get('/reports/staff-food-summary', { params });
      setData(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load report'); }
    finally { setLoading(false); }
  }, [month, branchId]);

  const fetchOrders = useCallback(async () => {
    setLoadingOrders(true);
    try {
      const params = { month };
      if (branchId && branchId !== 'all') params.branch_id = branchId;
      const { data } = await api.get('/reports/staff-food-orders', { params });
      setOrders(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load order log'); }
    finally { setLoadingOrders(false); }
  }, [month, branchId]);

  useEffect(() => {
    if (view === 'summary') fetchData(); else fetchOrders();
  }, [view, fetchData, fetchOrders]);

  const exportCsv = () => {
    const rows = (data?.rows || []).map((r) => ({
      staff: r.staff_member_name,
      orders: r.orders,
      qty: r.qty,
      value: r.value,
      items: (r.items || []).map((i) => `${i.qty}× ${i.name}`).join(' | '),
    }));
    if (!rows.length) { toast.info('No staff-meal records to export'); return; }
    exportToCsv(`staff_food_${month}`, rows, [
      { key: 'staff', label: 'Staff Member' },
      { key: 'orders', label: 'Orders' },
      { key: 'qty', label: 'Total Qty' },
      { key: 'value', label: 'Menu Value (SAR)' },
      { key: 'items', label: 'Items Eaten' },
    ]);
    toast.success(`Exported ${rows.length} staff row(s)`);
  };

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="staff-food-report-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground flex items-center gap-3">
              <UtensilsCrossed className="text-amber-600" size={26} /> Staff Food Report
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Monthly staff-meal consumption per employee — for HR / payroll deduction. Zero-revenue, inventory-deducting orders.</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex rounded-lg border overflow-hidden" data-testid="staff-food-view-toggle">
              <button onClick={() => setView('summary')} className={`px-3 py-1.5 text-xs font-medium ${view === 'summary' ? 'bg-amber-600 text-white' : 'bg-background text-muted-foreground'}`} data-testid="staff-food-view-summary">By Staff</button>
              <button onClick={() => setView('orders')} className={`px-3 py-1.5 text-xs font-medium ${view === 'orders' ? 'bg-amber-600 text-white' : 'bg-background text-muted-foreground'}`} data-testid="staff-food-view-orders">Order Log</button>
            </div>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40" data-testid="staff-food-month" />
            <Select value={branchId} onValueChange={setBranchId}>
              <SelectTrigger className="w-44" data-testid="staff-food-branch"><SelectValue placeholder="All branches" /></SelectTrigger>
              <SelectContent className="max-h-[60vh]">
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={() => (view === 'summary' ? fetchData() : fetchOrders())} data-testid="staff-food-refresh"><RefreshCw size={14} className="mr-1" />Refresh</Button>
            {view === 'summary' && <Button size="sm" onClick={exportCsv} data-testid="staff-food-export"><Download size={14} className="mr-1" />Export</Button>}
          </div>
        </div>

        {view === 'summary' && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Staff', value: data?.staff_count ?? 0, icon: Users },
            { label: 'Orders', value: data?.order_count ?? 0 },
            { label: 'Total Qty', value: data?.total_qty ?? 0 },
            { label: 'Total Value', value: fmt(data?.total_value), highlight: true },
          ].map((k, i) => (
            <Card key={i} data-testid={`staff-food-kpi-${i}`}>
              <CardContent className="p-4">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{k.label}</div>
                <div className={`text-lg font-bold mt-1 ${k.highlight ? 'text-amber-600' : 'text-foreground'}`}>{k.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>
        )}

        {view === 'summary' && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="staff-food-table">
                <thead>
                  <tr className="border-b bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                    <th className="px-4 py-2.5">Staff Member</th>
                    <th className="px-4 py-2.5 text-right">Orders</th>
                    <th className="px-4 py-2.5 text-right">Qty</th>
                    <th className="px-4 py-2.5 text-right">Menu Value</th>
                    <th className="px-4 py-2.5">Items Eaten</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>}
                  {!loading && (data?.rows || []).length === 0 && (
                    <tr><td colSpan={5} className="px-4 py-8 text-center text-muted-foreground" data-testid="staff-food-empty">No staff-meal records for {month}.</td></tr>
                  )}
                  {!loading && (data?.rows || []).map((r, i) => (
                    <tr key={r.staff_member_id || i} className="border-b hover:bg-muted/20" data-testid={`staff-food-row-${i}`}>
                      <td className="px-4 py-2.5 font-medium">{r.staff_member_name}</td>
                      <td className="px-4 py-2.5 text-right">{r.orders}</td>
                      <td className="px-4 py-2.5 text-right">{r.qty}</td>
                      <td className="px-4 py-2.5 text-right font-semibold">{fmt(r.value)}</td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">{(r.items || []).map((it) => `${it.qty}× ${it.name}`).join(', ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        )}

        {view === 'orders' && (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="staff-food-orders-table">
                <thead>
                  <tr className="border-b bg-muted/30 text-left text-xs uppercase text-muted-foreground">
                    <th className="px-4 py-2.5">Ref</th>
                    <th className="px-4 py-2.5">Date / Time</th>
                    <th className="px-4 py-2.5">Branch</th>
                    <th className="px-4 py-2.5">Staff</th>
                    <th className="px-4 py-2.5">Items</th>
                    <th className="px-4 py-2.5 text-right">Qty</th>
                    <th className="px-4 py-2.5 text-right">Value</th>
                    <th className="px-4 py-2.5">Recorded By</th>
                    <th className="px-4 py-2.5">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingOrders && <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>}
                  {!loadingOrders && (orders?.orders || []).length === 0 && (
                    <tr><td colSpan={9} className="px-4 py-8 text-center text-muted-foreground" data-testid="staff-food-orders-empty">No staff-food orders for {month}.</td></tr>
                  )}
                  {!loadingOrders && (orders?.orders || []).map((o, i) => (
                    <tr key={o.id || i} className="border-b hover:bg-muted/20" data-testid={`staff-food-order-row-${i}`}>
                      <td className="px-4 py-2.5 font-mono text-xs">{o.ref}</td>
                      <td className="px-4 py-2.5 text-xs">{o.created_at ? new Date(o.created_at).toLocaleString('en-GB') : ''}</td>
                      <td className="px-4 py-2.5">{o.branch_name}</td>
                      <td className="px-4 py-2.5 font-medium">{o.staff_member_name}</td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">{(o.items || []).map((it) => `${it.quantity}× ${it.name}`).join(', ')}</td>
                      <td className="px-4 py-2.5 text-right">{o.qty}</td>
                      <td className="px-4 py-2.5 text-right font-semibold">{fmt(o.value)}</td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground">{o.operator_email || '—'}</td>
                      <td className="px-4 py-2.5"><span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">{o.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
